import React, { useEffect, useState, useRef } from 'react';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import InquiryBanner from './InquiryBanner.jsx';
import banner from '../assets/banners/img3.avif';
import { FiSettings, FiCheckCircle, FiLayers, FiShield, FiTrendingUp, FiAward } from 'react-icons/fi';
import p7 from '../assets/products/trunnion.jpg';
import tmbvMatrix from '../assets/tmbv/tmbv_matrix.png';
import tmbv1 from '../assets/tmbv/tmbv1.PNG';
import '../css/trunnionBallValves.css';

const valveTypes = [
  {
    name: 'Standard Trunnion Ball Valves',
    description: 'Standard trunnion mounted ball valves designed for reliable operation in high-pressure applications.',
    features: ['High-pressure capability', 'Reduced operating torque', 'Fire-safe design', 'DBB configuration']
  },
  {
    name: 'Full Bore Trunnion Valves',
    description: 'Full-bore trunnion ball valves providing unrestricted flow with minimal pressure drop.',
    features: ['Full-bore design', 'Minimal pressure drop', 'Pigging capability', 'Optimal flow efficiency']
  },
  {
    name: 'Reduced Bore Trunnion Valves',
    description: 'Reduced-bore trunnion ball valves offering compact design with excellent flow control.',
    features: ['Compact design', 'Cost-effective', 'Standard flow capacity', 'Space-efficient']
  },
  {
    name: 'Fire-Safe Trunnion Valves',
    description: 'Fire-safe trunnion mounted ball valves with enhanced safety features for critical applications.',
    features: ['API 607/6FA certified', 'Emergency shut-off', 'Metal-to-metal sealing', 'Fire-resistant design']
  }
];

const specifications = [
  {
    title: 'Size Range',
    value: '2" – 24"',
    icon: <FiSettings />
  },
  {
    title: 'Pressure Rating',
    value: 'Class 150 – 2500',
    icon: <FiTrendingUp />
  },
  {
    title: 'Materials',
    value: 'Carbon Steel, Stainless Steel, Duplex, Alloy',
    icon: <FiLayers />
  },
  {
    title: 'Standards',
    value: 'API 6D, ASME B16.34',
    icon: <FiAward />
  }
];

const advantages = [
  {
    icon: <FiSettings />,
    title: 'High-Pressure Capability',
    description: 'Trunnion mounted design provides superior support for high-pressure applications, making these valves ideal for demanding industrial processes up to Class 2500.'
  },
  {
    icon: <FiShield />,
    title: 'Reduced Operating Torque',
    description: 'Trunnion support reduces operating torque significantly compared to floating ball valves, enabling easier operation and smaller actuator requirements.'
  },
  {
    icon: <FiAward />,
    title: 'Industry Standards',
    description: 'Manufactured to API 6D and ASME B16.34 standards, ensuring consistent quality and compliance with international specifications for ball valve applications.'
  },
  {
    icon: <FiTrendingUp />,
    title: 'Versatile Configurations',
    description: 'Available in DBB (Double Block & Bleed) and DIB (Double Isolation & Bleed) configurations, providing flexibility for various pipeline applications.'
  }
];

const applications = [
  {
    title: 'Oil & Gas Pipelines',
    description: 'Critical applications in oil and gas transmission pipelines requiring reliable isolation and flow control in high-pressure systems.'
  },
  {
    title: 'Refineries & Petrochemicals',
    description: 'Refineries and petrochemical plants requiring robust valves for hydrocarbon processing and high-pressure applications.'
  },
  {
    title: 'Power Generation',
    description: 'Power plants requiring high-pressure ball valves for steam and water systems with reliable operation and low maintenance.'
  },
  {
    title: 'Chemical Processing',
    description: 'Chemical processing plants handling aggressive chemicals where fire-safe design and reliable sealing are essential.'
  },
  {
    title: 'Water Treatment',
    description: 'Large-diameter water treatment and distribution systems requiring reliable isolation valves for maintenance operations.'
  },
  {
    title: 'Marine & Offshore',
    description: 'Marine applications and offshore platforms where corrosion resistance and high-pressure capability are critical in harsh environments.'
  }
];

const features = [
  'High-pressure capability',
  'Reduced operating torque',
  'Fire-safe design',
  'DBB & DIB configurations',
  'Actuator ready',
  'Full-bore & reduced-bore options',
  'Metal-to-metal sealing',
  'Blow-out proof stem'
];

const TrunnionBallValves = () => {
  const sectionsRef = useRef([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.05,
      rootMargin: '0px 0px 0px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('scroll-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sections = sectionsRef.current;
    
    // Check immediately if sections are already in viewport
    const checkVisibility = () => {
      sections.forEach((section) => {
        if (section) {
          const rect = section.getBoundingClientRect();
          const isInViewport = rect.top < window.innerHeight + 100 && rect.bottom > -100;
          if (isInViewport && !section.classList.contains('scroll-visible')) {
            section.classList.add('scroll-visible');
          }
        }
      });
    };
    
    // Check on mount
    checkVisibility();
    
    // Observe sections for future visibility changes
    sections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    // Fallback: Make sections visible after a delay if observer doesn't trigger
    const fallbackTimeout = setTimeout(() => {
      checkVisibility();
    }, 500);

    return () => {
      clearTimeout(fallbackTimeout);
      sections.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Header />
      <div className="tmbv-banner" style={{ minHeight: '500px', position: 'relative' }}>
        <img
          src={banner}
          alt="Trunnion Mounted Ball Valves Banner"
          className="tmbv-banner__img"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div className="tmbv-banner__overlay"></div>
        <div className="tmbv-banner__content">
          <h2 className="tmbv-banner__title">Trunnion Mounted Ball Valves</h2>
          <div className="tmbv-banner__subtitle">
            Heavy-duty trunnion mounted ball valves designed for high-pressure and large-diameter applications
          </div>
        </div>
      </div>

      <div 
        ref={(el) => (sectionsRef.current[0] = el)}
        className="scroll-section"
      >
        <section className="tmbv-hero">
          <div className="tmbv-container">
            <div className="tmbv-hero-layout">
              <div className="tmbv-hero-media">
                <img src={p7} alt="Trunnion Mounted Ball Valves" className="tmbv-hero-img" />
              </div>
              <div className="tmbv-hero-body">
                <h2 className="tmbv-hero-title">Trunnion Mounted Ball  Valve</h2>
                <p className="tmbv-hero-desc">
                  Trunnion Mounted Ball valve is used for off application. Most used standard of 
                  reference is API 6D. Materials available include carbon steel, low temperature carbon 
                  steel, low alloy steel, austenitic stainless steels, austenitic-ferritic duplex stainless 
                  steel, nickel alloys with soft seat as sealing component. Due higher torque in floating 
                  ball piping components, for higher sizes trunnion mounted ball piping components 
                  are recommended.
                </p>
              </div>
            </div>
            <div className="tmbv-hero-specs-row">
              <div className="tmbv-spec-card-horizontal">
                <h3>Product Range</h3>
                <ul>
                  <li><strong>Piping component size:</strong> 2" ~ 24" (DN50-DN600)</li>
                  <li><strong>Pressure:</strong> ANSI Class 150~2500</li>
                  <li><strong>Temperature:</strong> -196℃~ 150℃</li>
                  <li><strong>Body Materials:</strong> Carbon Steel, Stainless Steel, Alloy Steel, Duplex Steel</li>
                  <li><strong>Trim (Disc/Stem):</strong> Carbon Steel, Stainless Steel, Duplex Steel, Alloy Steel</li>
                  <li><strong>Seat:</strong> RPTFE, TFM, Nylon, Devlon and PEEK</li>
                  <li><strong>Metal seated:</strong> TCC, CrC and stellite coating on Ball and Seat (HVOF)</li>
                </ul>
              </div>

              <div className="tmbv-spec-card-horizontal">
                <h3>Technical Specifications</h3>
                <ul>
                  <li><strong>Design Standard:</strong> API 6D / ASME B 16.34</li>
                  <li><strong>Structure length:</strong> ANSI B16.10/API 6D</li>
                  <li><strong>Pipe flanges:</strong> ANSI B16.5</li>
                  <li><strong>Butt end size:</strong> ANSI B16.25</li>
                  <li><strong>Inspection and Testing:</strong> API 6D/ISO 5208</li>
                  <li><strong>Transmission:</strong> manual, electric, Pneumatic actuator</li>
                </ul>
              </div>

              <div className="tmbv-spec-card-horizontal">
                <h3>Design Features</h3>
                <ul>
                  <li>Anti Blowout</li>
                  <li>Bolted Bonnet</li>
                  <li>Self-Cavity Relief seats</li>
                  <li>Extended Bonnet for Cryogenic Services</li>
                  <li>Locking Device as an Optional</li>
                  <li>Extended stem construction for Underground Service</li>
                  <li>Double Block & Bleed arrangement</li>
                  <li>Double Isolation 1 & 2 on request</li>
                  <li>Seat and stem pocket CRA overlay on request</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Product Availability Matrix Section */}
      <div className="tmbv-specification-table-section">
        <div className="tmbv-container">
          <h2 className="tmbv-table-title">Product Availability Matrix</h2>
          <img src={tmbvMatrix} alt="Product Availability Matrix" />
          <p className="tmbv-legend">Legends : BB - Bolted Bonnet, BC - Bolted Cover, PS - Pressure Seal</p>
        </div>
      </div>

      {/* Materials of Construction Section */}
      <div 
        ref={(el) => (sectionsRef.current[1] = el)}
        className="scroll-section"
      >
        <section className="tmbv-material-section">
          <div className="tmbv-container">
            <h2 className="tmbv-table-title">Materials of Construction</h2>
            <div className="tmbv-material-content">
              <div className="tmbv-material-image">
                <img src={tmbv1} alt="Trunnion Ball Valve Parts" />
              </div>
              <div className="tmbv-material-table-container">
                <table className="tmbv-material-table">
                  <thead>
                    <tr>
                      <th>PART NO</th>
                      <th>PART</th>
                      <th>MATERIAL OPTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>1</td>
                      <td>BODY</td>
                      <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                    </tr>
                    <tr>
                      <td>2</td>
                      <td>BODY END COVER</td>
                      <td>ASTM - A 216 WCB / WCC / LF2 / A 105 / A 351 CF8M / A 351 CF3M / A 352 LCB / LCC / DUPLEX SS / INCONEL / 254 SMO.</td>
                    </tr>
                    <tr>
                      <td>3</td>
                      <td>BALL</td>
                      <td>ASTM - A 216 WCB / WCC + ENP / A 351 CF8M / A 351 CF3M / A 217 CA 15 / DUPLEX SS / INCONEL / MONEL.</td>
                    </tr>
                    <tr>
                      <td>4</td>
                      <td>STUFFING BOX</td>
                      <td>ASTM - A 216 WCB / A351 CF8M / A 351 CF3M / A 352 LCB.</td>
                    </tr>
                    <tr>
                      <td>5</td>
                      <td>STEM</td>
                      <td>A 479 SS 316 / A 479 SS 316L / A 182 F 6A / A 564 TYPE 630 / A 479 SS 410 / AISI 4140 + ENP / ASTM A 182 F51, F53, F44 / INCONEL (625, 825, 718).</td>
                    </tr>
                    <tr>
                      <td>6</td>
                      <td>SEAT</td>
                      <td>ASTM - A 105 + ENP / A 182 F316 / ASTM A 182 F6A / DUPLEX SS / INCONEL / F44 / LF2.</td>
                    </tr>
                    <tr>
                      <td>7</td>
                      <td>SEAT INSERT</td>
                      <td>RPTFE / NYLON / PEEK / DEVLON / PCTFE.</td>
                    </tr>
                    <tr>
                      <td>8</td>
                      <td>STEM GASKET</td>
                      <td>GRAPHITE / LIP SEAL / SPW SS316 + GRAPHOIL.</td>
                    </tr>
                    <tr>
                      <td>9</td>
                      <td>BODY GASKET</td>
                      <td>GRAPHITE / REINFORCED SPIRAL-WOUND SS 316 GRAPHITE / LIP SEAL.</td>
                    </tr>
                    <tr>
                      <td>10</td>
                      <td>STUFFING BOX GASKET</td>
                      <td>GRAPHITE / LIP SEAL / SPW SS316 + GRAPHOIL.</td>
                    </tr>
                    <tr>
                      <td>11</td>
                      <td>TRUNNION GASKET</td>
                      <td>GRAPHITE / LIP SEAL / SPW SS316 + GRAPHOIL.</td>
                    </tr>
                    <tr>
                      <td>12</td>
                      <td>O RING</td>
                      <td>VITON (SPECIAL O-RING ON REQUEST).</td>
                    </tr>
                    <tr>
                      <td>13</td>
                      <td>TRUNNION</td>
                      <td>A 479 SS 316 / A 479 SS 316L / A 182 F 304 / A 182 F 316 / A 182 F 410 / A 564n TYPE 630 / A 105 / LF2 / DUPLEX SS / INCONEL.</td>
                    </tr>
                    <tr>
                      <td>14</td>
                      <td>ISO MOUNTING FLANGE</td>
                      <td>STEEL.</td>
                    </tr>
                    <tr>
                      <td>15</td>
                      <td>SPRINGS</td>
                      <td>ASTM - A 313 SS 302 / ASTM B 637 (INCONEL 750) / INCONEL 718.</td>
                    </tr>
                    <tr>
                      <td>16</td>
                      <td>THRUST WASHER</td>
                      <td>PHOSPHOR BRONZE / A 479 SS 316 + BRONZE + PTFE COATED / A 479 SS 316 + PTFE COATED.</td>
                    </tr>
                    <tr>
                      <td>17</td>
                      <td>STUDS / BOLTS / CAP SCREW</td>
                      <td>ASTM - A 320 L7, L7M / A 193 B8M / A 193 B7 / A 193 GR. B7M.</td>
                    </tr>
                    <tr>
                      <td>18</td>
                      <td>NUT</td>
                      <td>ASTM - A 194 GR 7M / A 194 GR 8M / A 194 2H / A 194 GR 2HM.</td>
                    </tr>
                    <tr>
                      <td>19</td>
                      <td>BEARING</td>
                      <td>A 479 SS 316 + PTFE COATED.</td>
                    </tr>
                    <tr>
                      <td>20</td>
                      <td>INTERMEDIATE PLATE</td>
                      <td>STEEL.</td>
                    </tr>
                    <tr>
                      <td>21</td>
                      <td>SUPPORT STAND</td>
                      <td>STEEL.</td>
                    </tr>
                    <tr>
                      <td>22</td>
                      <td>LIFTING HOOK</td>
                      <td>STEEL.</td>
                    </tr>
                    <tr>
                      <td>23</td>
                      <td>DRAIN PLUG/ NEEDLE VALVE</td>
                      <td>STANDARD.</td>
                    </tr>
                  </tbody>
                </table>
                <p className="tmbv-material-note">Note: Materials not listed above can be offered on request.</p>
              </div>
            </div>

            {/* Dimensional Details Tables */}
            <div className="tmbv-dimensional-details-section">
              {/* 150 Class Table */}
              <div className="tmbv-dimensional-table-wrapper">
                <h3 className="tmbv-dimensional-title">TRUNNION MOUNTED BALL VALVE DIMENSIONAL DETAILS FOR 150 CLASS</h3>
                <div className="tmbv-table-scroll-container">
                  <table className="tmbv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th>ØB1</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ΦH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>H1</th>
                        <th>O</th>
                        <th>G</th>
                        <th>ØC</th>
                        <th colSpan="2">Weight (Kg)</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>Bare Stem</th>
                        <th>Gear</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050) x 1 1/2" (040)</td><td>49</td><td>38</td><td>178</td><td>216</td><td>191</td><td>152</td><td>120.6</td><td>19.1</td><td>4</td><td>91.9</td><td>14.2</td><td>1.5</td><td>74</td><td>84</td><td>200</td><td>-</td><td>8</td><td>-</td></tr>
                      <tr><td>2" (050)</td><td>49</td><td>49</td><td>178</td><td>216</td><td>191</td><td>152</td><td>120.6</td><td>19.1</td><td>4</td><td>91.9</td><td>14.2</td><td>1.5</td><td>74</td><td>84</td><td>200</td><td>-</td><td>8</td><td>-</td></tr>
                      <tr><td>3" (080) x 2" (050)</td><td>74</td><td>49</td><td>216</td><td>254</td><td>229</td><td>190</td><td>152.4</td><td>19.1</td><td>4</td><td>127</td><td>17.5</td><td>1.5</td><td>74</td><td>84</td><td>200</td><td>-</td><td>12</td><td>-</td></tr>
                      <tr><td>3" (080)</td><td>74</td><td>74</td><td>216</td><td>254</td><td>229</td><td>190</td><td>152.4</td><td>19.1</td><td>4</td><td>127</td><td>17.5</td><td>1.5</td><td>110</td><td>143</td><td>-</td><td>250</td><td>20</td><td>14.5</td></tr>
                      <tr><td>4" (100) x 3" (080)</td><td>100</td><td>74</td><td>241</td><td>292</td><td>267</td><td>229</td><td>190.5</td><td>19.1</td><td>8</td><td>157.2</td><td>22.4</td><td>1.5</td><td>110</td><td>143</td><td>-</td><td>250</td><td>28</td><td>14.5</td></tr>
                      <tr><td>4" (100)</td><td>100</td><td>100</td><td>241</td><td>292</td><td>267</td><td>229</td><td>190.5</td><td>19.1</td><td>8</td><td>157.2</td><td>22.4</td><td>1.5</td><td>145</td><td>178</td><td>-</td><td>300</td><td>35</td><td>14.5</td></tr>
                      <tr><td>6" (150) x 4" (100)</td><td>153</td><td>100</td><td>292</td><td>406</td><td>381</td><td>279</td><td>241.3</td><td>22.2</td><td>8</td><td>215.9</td><td>23.9</td><td>1.5</td><td>145</td><td>178</td><td>-</td><td>300</td><td>42</td><td>14.5</td></tr>
                      <tr><td>6" (150)</td><td>153</td><td>153</td><td>292</td><td>406</td><td>381</td><td>279</td><td>241.3</td><td>22.2</td><td>8</td><td>215.9</td><td>23.9</td><td>1.5</td><td>199</td><td>259</td><td>-</td><td>400</td><td>65</td><td>43.9</td></tr>
                      <tr><td>8" (200) x 6" (150)</td><td>204</td><td>153</td><td>406</td><td>495</td><td>470</td><td>343</td><td>298.4</td><td>22.2</td><td>8</td><td>269.7</td><td>26.9</td><td>1.5</td><td>199</td><td>259</td><td>-</td><td>400</td><td>79</td><td>43.9</td></tr>
                      <tr><td>8" (200)</td><td>204</td><td>204</td><td>406</td><td>495</td><td>470</td><td>343</td><td>298.4</td><td>22.2</td><td>8</td><td>269.7</td><td>26.9</td><td>1.5</td><td>214</td><td>275</td><td>-</td><td>500</td><td>95</td><td>43.9</td></tr>
                      <tr><td>10" (250) x 8" (200)</td><td>254</td><td>204</td><td>495</td><td>622</td><td>597</td><td>406</td><td>362</td><td>25.4</td><td>12</td><td>323.9</td><td>28.4</td><td>1.5</td><td>214</td><td>275</td><td>-</td><td>500</td><td>131</td><td>43.9</td></tr>
                      <tr><td>10" (250)</td><td>254</td><td>254</td><td>495</td><td>622</td><td>597</td><td>406</td><td>362</td><td>25.4</td><td>12</td><td>323.9</td><td>28.4</td><td>1.5</td><td>241</td><td>312</td><td>-</td><td>550</td><td>160</td><td>89.8</td></tr>
                      <tr><td>12" (300) x 10" (250)</td><td>305</td><td>254</td><td>622</td><td>698</td><td>673</td><td>483</td><td>431.8</td><td>25.4</td><td>12</td><td>381</td><td>30.2</td><td>1.5</td><td>241</td><td>312</td><td>-</td><td>550</td><td>177</td><td>89.8</td></tr>
                      <tr><td>12" (300)</td><td>305</td><td>305</td><td>622</td><td>698</td><td>673</td><td>483</td><td>431.8</td><td>25.4</td><td>12</td><td>381</td><td>30.2</td><td>1.5</td><td>282</td><td>381</td><td>-</td><td>600</td><td>220</td><td>89.8</td></tr>
                      <tr><td>14" (350) x 12" (300)</td><td>337</td><td>305</td><td>698</td><td>787</td><td>762</td><td>533</td><td>476.2</td><td>28.6</td><td>12</td><td>412.8</td><td>33.3</td><td>1.5</td><td>282</td><td>381</td><td>-</td><td>600</td><td>282</td><td>89.8</td></tr>
                      <tr><td>14" (350) x 10" (250)</td><td>337</td><td>254</td><td>698</td><td>787</td><td>762</td><td>533</td><td>476.2</td><td>28.6</td><td>12</td><td>412.8</td><td>33.3</td><td>1.5</td><td>241</td><td>312</td><td>-</td><td>550</td><td>220</td><td>89.8</td></tr>
                      <tr><td>14" (350)</td><td>337</td><td>337</td><td>698</td><td>787</td><td>762</td><td>533</td><td>476.2</td><td>28.6</td><td>12</td><td>412.8</td><td>33.3</td><td>1.5</td><td>329</td><td>425</td><td>-</td><td>600</td><td>380</td><td>114</td></tr>
                      <tr><td>16" (400) x 14" (350)</td><td>388</td><td>337</td><td>787</td><td>914</td><td>889</td><td>597</td><td>539.8</td><td>28.6</td><td>16</td><td>469.9</td><td>35.1</td><td>1.5</td><td>329</td><td>425</td><td>-</td><td>600</td><td>380</td><td>114</td></tr>
                      <tr><td>16" (400) x 12" (300)</td><td>388</td><td>305</td><td>787</td><td>914</td><td>889</td><td>597</td><td>539.8</td><td>28.6</td><td>16</td><td>469.9</td><td>35.1</td><td>1.5</td><td>282</td><td>381</td><td>-</td><td>600</td><td>282</td><td>89.8</td></tr>
                      <tr><td>16" (400)</td><td>388</td><td>388</td><td>787</td><td>914</td><td>889</td><td>597</td><td>539.8</td><td>28.6</td><td>16</td><td>469.9</td><td>35.1</td><td>1.5</td><td>381</td><td>478</td><td>-</td><td>750</td><td>542</td><td>220</td></tr>
                      <tr><td>18" (450) x 16" (400)</td><td>439</td><td>388</td><td>914</td><td>978</td><td>953</td><td>635</td><td>577.8</td><td>31.8</td><td>16</td><td>533.4</td><td>38.1</td><td>1.5</td><td>381</td><td>478</td><td>-</td><td>750</td><td>632</td><td>220</td></tr>
                      <tr><td>18" (450) x 14" (350)</td><td>439</td><td>337</td><td>914</td><td>978</td><td>953</td><td>635</td><td>577.8</td><td>31.8</td><td>16</td><td>533.4</td><td>38.1</td><td>1.5</td><td>329</td><td>425</td><td>-</td><td>600</td><td>380</td><td>114</td></tr>
                      <tr><td>18" (450)</td><td>439</td><td>439</td><td>914</td><td>978</td><td>953</td><td>635</td><td>577.8</td><td>31.8</td><td>16</td><td>533.4</td><td>38.1</td><td>1.5</td><td>425</td><td>550</td><td>-</td><td>750</td><td>632</td><td>220</td></tr>
                      <tr><td>20" (500) x 18" (450)</td><td>489</td><td>439</td><td>978</td><td>978</td><td>953</td><td>693</td><td>635</td><td>31.8</td><td>20</td><td>584.2</td><td>41.1</td><td>1.5</td><td>425</td><td>550</td><td>-</td><td>750</td><td>855</td><td>220</td></tr>
                      <tr><td>20" (500) x 16" (400)</td><td>489</td><td>388</td><td>978</td><td>978</td><td>953</td><td>693</td><td>635</td><td>31.8</td><td>20</td><td>584.2</td><td>41.1</td><td>1.5</td><td>381</td><td>478</td><td>-</td><td>750</td><td>542</td><td>220</td></tr>
                      <tr><td>20" (500)</td><td>489</td><td>489</td><td>978</td><td>978</td><td>953</td><td>693</td><td>635</td><td>31.8</td><td>20</td><td>584.2</td><td>41.1</td><td>1.5</td><td>460</td><td>578</td><td>-</td><td>750</td><td>855</td><td>296</td></tr>
                      <tr><td>24" (600) x 20" (500)</td><td>591</td><td>489</td><td>1295</td><td>1295</td><td>1270</td><td>813</td><td>749.3</td><td>35.1</td><td>20</td><td>692.2</td><td>46</td><td>1.5</td><td>460</td><td>578</td><td>-</td><td>1006</td><td>970</td><td>296</td></tr>
                      <tr><td>24" (600)</td><td>591</td><td>591</td><td>1295</td><td>1295</td><td>1270</td><td>813</td><td>749.3</td><td>35.1</td><td>20</td><td>692.2</td><td>46</td><td>1.5</td><td>589</td><td>730</td><td>-</td><td>750</td><td>3250</td><td>296</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 300 Class Table */}
              <div className="tmbv-dimensional-table-wrapper">
                <h3 className="tmbv-dimensional-title">TRUNNION MOUNTED BALL VALVE DIMENSIONAL DETAILS FOR 300 CLASS</h3>
                <div className="tmbv-table-scroll-container">
                  <table className="tmbv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th>ØB1</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>H1</th>
                        <th>O</th>
                        <th>G</th>
                        <th>ØC</th>
                        <th colSpan="2">Weight (Kg)</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>Bare Stem</th>
                        <th>Gear</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050) x 1 1/2" (040)</td><td>49</td><td>38</td><td>216</td><td>216</td><td>232</td><td>165</td><td>127</td><td>19.1</td><td>8</td><td>91.9</td><td>20.6</td><td>1.5</td><td>110</td><td>143</td><td>400</td><td>-</td><td>23</td><td>-</td></tr>
                      <tr><td>2" (050)</td><td>49</td><td>49</td><td>216</td><td>216</td><td>232</td><td>165</td><td>127</td><td>19.1</td><td>8</td><td>91.9</td><td>20.6</td><td>1.5</td><td>110</td><td>143</td><td>400</td><td>-</td><td>23</td><td>-</td></tr>
                      <tr><td>3" (080) x 2" (050)</td><td>74</td><td>49</td><td>283</td><td>283</td><td>298</td><td>210</td><td>168.1</td><td>22.2</td><td>8</td><td>127</td><td>26.9</td><td>1.5</td><td>110</td><td>143</td><td>400</td><td>-</td><td>40</td><td>-</td></tr>
                      <tr><td>3" (080)</td><td>74</td><td>74</td><td>283</td><td>283</td><td>298</td><td>210</td><td>168.1</td><td>22.2</td><td>8</td><td>127</td><td>26.9</td><td>1.5</td><td>199</td><td>259</td><td>-</td><td>250</td><td>62</td><td>14.5</td></tr>
                      <tr><td>4" (100) x 3" (080)</td><td>100</td><td>74</td><td>305</td><td>305</td><td>321</td><td>254</td><td>200.2</td><td>22.2</td><td>8</td><td>157.2</td><td>30.2</td><td>1.5</td><td>199</td><td>259</td><td>-</td><td>250</td><td>78</td><td>14.5</td></tr>
                      <tr><td>4" (100)</td><td>100</td><td>100</td><td>305</td><td>305</td><td>321</td><td>254</td><td>200.2</td><td>22.2</td><td>8</td><td>157.2</td><td>30.2</td><td>1.5</td><td>214</td><td>275</td><td>-</td><td>300</td><td>99</td><td>14.5</td></tr>
                      <tr><td>6" (150) x 4" (100)</td><td>150</td><td>100</td><td>403</td><td>457</td><td>419</td><td>318</td><td>269.7</td><td>22.2</td><td>12</td><td>215.9</td><td>35.1</td><td>1.5</td><td>214</td><td>275</td><td>-</td><td>300</td><td>125</td><td>14.5</td></tr>
                      <tr><td>6" (150)</td><td>150</td><td>150</td><td>403</td><td>457</td><td>419</td><td>318</td><td>269.7</td><td>22.2</td><td>12</td><td>215.9</td><td>35.1</td><td>1.5</td><td>241</td><td>312</td><td>-</td><td>400</td><td>160</td><td>43.9</td></tr>
                      <tr><td>8" (200) x 6" (150)</td><td>200</td><td>150</td><td>502</td><td>521</td><td>518</td><td>381</td><td>330.2</td><td>25.4</td><td>12</td><td>269.7</td><td>39.6</td><td>1.5</td><td>241</td><td>312</td><td>-</td><td>400</td><td>309</td><td>43.9</td></tr>
                      <tr><td>8" (200)</td><td>201</td><td>201</td><td>502</td><td>521</td><td>518</td><td>381</td><td>330.2</td><td>25.4</td><td>12</td><td>269.7</td><td>39.6</td><td>1.5</td><td>269</td><td>340</td><td>-</td><td>500</td><td>343</td><td>43.9</td></tr>
                      <tr><td>10" (250) x 8" (200)</td><td>252</td><td>201</td><td>568</td><td>559</td><td>584</td><td>444</td><td>387.4</td><td>28.6</td><td>16</td><td>323.9</td><td>46</td><td>1.5</td><td>269</td><td>340</td><td>-</td><td>500</td><td>390</td><td>43.9</td></tr>
                      <tr><td>10" (250)</td><td>252</td><td>252</td><td>568</td><td>559</td><td>584</td><td>444</td><td>387.4</td><td>28.6</td><td>16</td><td>323.9</td><td>46</td><td>1.5</td><td>329</td><td>425</td><td>-</td><td>550</td><td>449</td><td>89.8</td></tr>
                      <tr><td>12" (300) x 10" (250)</td><td>303</td><td>252</td><td>648</td><td>635</td><td>664</td><td>521</td><td>450.8</td><td>31.8</td><td>16</td><td>381</td><td>30.2</td><td>1.5</td><td>329</td><td>425</td><td>-</td><td>550</td><td>561</td><td>89.8</td></tr>
                      <tr><td>12" (300)</td><td>303</td><td>303</td><td>648</td><td>635</td><td>664</td><td>521</td><td>450.8</td><td>31.8</td><td>16</td><td>381</td><td>30.2</td><td>1.5</td><td>381</td><td>478</td><td>-</td><td>600</td><td>654</td><td>89.8</td></tr>
                      <tr><td>14" (350) x 12" (300)</td><td>334</td><td>303</td><td>762</td><td>762</td><td>778</td><td>584</td><td>514.4</td><td>31.8</td><td>20</td><td>412.8</td><td>52.3</td><td>1.5</td><td>381</td><td>478</td><td>-</td><td>600</td><td>776</td><td>89.8</td></tr>
                      <tr><td>14" (350) x 10" (250)</td><td>334</td><td>252</td><td>762</td><td>762</td><td>778</td><td>584</td><td>514.4</td><td>31.8</td><td>20</td><td>412.8</td><td>52.3</td><td>1.5</td><td>329</td><td>425</td><td>-</td><td>550</td><td>606</td><td>89.8</td></tr>
                      <tr><td>14" (350)</td><td>334</td><td>334</td><td>762</td><td>762</td><td>778</td><td>584</td><td>514.4</td><td>31.8</td><td>20</td><td>412.8</td><td>52.3</td><td>1.5</td><td>399</td><td>516</td><td>-</td><td>600</td><td>988</td><td>114</td></tr>
                      <tr><td>16" (400) x 14" (350)</td><td>385</td><td>334</td><td>838</td><td>838</td><td>854</td><td>648</td><td>571.5</td><td>35.1</td><td>20</td><td>469.9</td><td>55.6</td><td>1.5</td><td>399</td><td>516</td><td>-</td><td>600</td><td>988</td><td>114</td></tr>
                      <tr><td>16" (400) x 12" (300)</td><td>385</td><td>303</td><td>838</td><td>838</td><td>854</td><td>648</td><td>571.5</td><td>35.1</td><td>20</td><td>469.9</td><td>55.6</td><td>1.5</td><td>381</td><td>478</td><td>-</td><td>600</td><td>832</td><td>89.8</td></tr>
                      <tr><td>16" (400)</td><td>385</td><td>385</td><td>838</td><td>838</td><td>854</td><td>648</td><td>571.5</td><td>35.1</td><td>20</td><td>469.9</td><td>55.6</td><td>1.5</td><td>460</td><td>578</td><td>-</td><td>750</td><td>1000</td><td>220</td></tr>
                      <tr><td>18" (450) x 16" (400)</td><td>436</td><td>385</td><td>914</td><td>914</td><td>930</td><td>711</td><td>628.6</td><td>35.1</td><td>24</td><td>533.4</td><td>58.7</td><td>1.5</td><td>460</td><td>578</td><td>-</td><td>750</td><td>1397</td><td>220</td></tr>
                      <tr><td>18" (450) x 14" (350)</td><td>436</td><td>334</td><td>914</td><td>914</td><td>930</td><td>711</td><td>628.6</td><td>35.1</td><td>24</td><td>533.4</td><td>58.7</td><td>1.5</td><td>399</td><td>516</td><td>-</td><td>600</td><td>1128</td><td>114</td></tr>
                      <tr><td>18" (450)</td><td>436</td><td>436</td><td>914</td><td>914</td><td>930</td><td>711</td><td>628.6</td><td>35.1</td><td>24</td><td>533.4</td><td>58.7</td><td>1.5</td><td>497</td><td>677</td><td>-</td><td>750</td><td>1552</td><td>220</td></tr>
                      <tr><td>20" (500) x 18" (450)</td><td>487</td><td>436</td><td>991</td><td>991</td><td>1010</td><td>775</td><td>685.8</td><td>35.1</td><td>24</td><td>584.2</td><td>62</td><td>1.5</td><td>497</td><td>677</td><td>-</td><td>750</td><td>2034</td><td>220</td></tr>
                      <tr><td>20" (500) x 16" (400)</td><td>487</td><td>385</td><td>991</td><td>991</td><td>1010</td><td>775</td><td>685.8</td><td>35.1</td><td>24</td><td>584.2</td><td>62</td><td>1.5</td><td>460</td><td>578</td><td>-</td><td>750</td><td>1512</td><td>220</td></tr>
                      <tr><td>20" (500)</td><td>487</td><td>487</td><td>991</td><td>991</td><td>1010</td><td>775</td><td>685.8</td><td>35.1</td><td>24</td><td>584.2</td><td>62</td><td>1.5</td><td>518</td><td>699</td><td>-</td><td>750</td><td>2306</td><td>296</td></tr>
                      <tr><td>24" (600) x 20" (500)</td><td>589</td><td>487</td><td>1143</td><td>1143</td><td>1165</td><td>914</td><td>812.8</td><td>41.1</td><td>24</td><td>692.2</td><td>68.3</td><td>1.5</td><td>518</td><td>699</td><td>-</td><td>1006</td><td>2793</td><td>296</td></tr>
                      <tr><td>24" (600)</td><td>589</td><td>589</td><td>1143</td><td>1143</td><td>1165</td><td>914</td><td>812.8</td><td>41.1</td><td>24</td><td>692.2</td><td>68.3</td><td>1.5</td><td>648</td><td>828</td><td>-</td><td>1006</td><td>3610</td><td>439</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 600 Class Table */}
              <div className="tmbv-dimensional-table-wrapper">
                <h3 className="tmbv-dimensional-title">TRUNNION MOUNTED BALL VALVE DIMENSIONAL DETAILS FOR 600 CLASS</h3>
                <div className="tmbv-table-scroll-container">
                  <table className="tmbv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th>ØB1</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>H1</th>
                        <th>O</th>
                        <th>G</th>
                        <th>ØC</th>
                        <th colSpan="2">Weight (Kg)</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>Bare Stem</th>
                        <th>Gear</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050) x 1 1/2" (040)</td><td>49</td><td>38</td><td>292</td><td>292</td><td>295</td><td>165</td><td>127</td><td>19.1</td><td>8</td><td>91.9</td><td>25.4</td><td>6.4</td><td>110</td><td>143</td><td>400</td><td>350</td><td>24</td><td>17</td></tr>
                      <tr><td>2" (050)</td><td>49</td><td>49</td><td>292</td><td>292</td><td>295</td><td>165</td><td>127</td><td>19.1</td><td>8</td><td>91.9</td><td>25.4</td><td>6.4</td><td>110</td><td>143</td><td>400</td><td>350</td><td>24</td><td>17</td></tr>
                      <tr><td>3" (080) x 2" (050)</td><td>74</td><td>49</td><td>356</td><td>356</td><td>359</td><td>210</td><td>168.1</td><td>22.2</td><td>8</td><td>127</td><td>31.8</td><td>6.4</td><td>110</td><td>143</td><td>400</td><td>350</td><td>34</td><td>17</td></tr>
                      <tr><td>3" (080)</td><td>74</td><td>74</td><td>356</td><td>356</td><td>359</td><td>210</td><td>168.1</td><td>22.2</td><td>8</td><td>127</td><td>31.8</td><td>6.4</td><td>145</td><td>259</td><td>500</td><td>550</td><td>54</td><td>54</td></tr>
                      <tr><td>4" (100) x 3" (080)</td><td>100</td><td>74</td><td>432</td><td>432</td><td>435</td><td>273</td><td>215.9</td><td>25.4</td><td>8</td><td>157.2</td><td>38.1</td><td>6.4</td><td>145</td><td>259</td><td>500</td><td>550</td><td>77</td><td>54</td></tr>
                      <tr><td>4" (100)</td><td>100</td><td>100</td><td>432</td><td>432</td><td>435</td><td>273</td><td>215.9</td><td>25.4</td><td>8</td><td>157.2</td><td>38.1</td><td>6.4</td><td>199</td><td>275</td><td>1000</td><td>600</td><td>83</td><td>90</td></tr>
                      <tr><td>6" (150) x 4" (100)</td><td>150</td><td>100</td><td>559</td><td>559</td><td>562</td><td>356</td><td>292.1</td><td>28.6</td><td>12</td><td>215.9</td><td>47.8</td><td>6.4</td><td>199</td><td>275</td><td>1000</td><td>600</td><td>120</td><td>90</td></tr>
                      <tr><td>6" (150)</td><td>150</td><td>150</td><td>559</td><td>559</td><td>562</td><td>356</td><td>292.1</td><td>28.6</td><td>12</td><td>215.9</td><td>47.8</td><td>6.4</td><td>241</td><td>312</td><td>1000</td><td>600</td><td>161</td><td>114</td></tr>
                      <tr><td>8" (200) x 6" (150)</td><td>200</td><td>150</td><td>660</td><td>660</td><td>663</td><td>419</td><td>349.2</td><td>31.8</td><td>12</td><td>269.7</td><td>55.6</td><td>6.4</td><td>241</td><td>312</td><td>1000</td><td>600</td><td>239</td><td>114</td></tr>
                      <tr><td>8" (200)</td><td>201</td><td>201</td><td>660</td><td>660</td><td>663</td><td>419</td><td>349.2</td><td>31.8</td><td>12</td><td>269.7</td><td>55.6</td><td>6.4</td><td>282</td><td>340</td><td>1000</td><td>750</td><td>328</td><td>220</td></tr>
                      <tr><td>10" (250) x 8" (200)</td><td>252</td><td>201</td><td>787</td><td>787</td><td>790</td><td>508</td><td>431.8</td><td>35.1</td><td>16</td><td>323.9</td><td>63.5</td><td>6.4</td><td>282</td><td>340</td><td>1000</td><td>750</td><td>484</td><td>220</td></tr>
                      <tr><td>10" (250)</td><td>252</td><td>252</td><td>787</td><td>787</td><td>790</td><td>508</td><td>431.8</td><td>35.1</td><td>16</td><td>323.9</td><td>63.5</td><td>6.4</td><td>329</td><td>425</td><td>1000</td><td>750</td><td>589</td><td>295</td></tr>
                      <tr><td>12" (300) x 10" (250)</td><td>303</td><td>252</td><td>838</td><td>838</td><td>841</td><td>559</td><td>489</td><td>35.1</td><td>20</td><td>381</td><td>66.5</td><td>6.4</td><td>329</td><td>425</td><td>1000</td><td>750</td><td>599</td><td>295</td></tr>
                      <tr><td>12" (300)</td><td>303</td><td>303</td><td>838</td><td>838</td><td>841</td><td>559</td><td>489</td><td>35.1</td><td>20</td><td>381</td><td>66.5</td><td>6.4</td><td>381</td><td>478</td><td>1000</td><td>750</td><td>649</td><td>296</td></tr>
                      <tr><td>14" (350) x 12" (300)</td><td>334</td><td>303</td><td>889</td><td>889</td><td>892</td><td>603</td><td>527</td><td>38.1</td><td>20</td><td>412.8</td><td>69.8</td><td>6.4</td><td>381</td><td>478</td><td>1000</td><td>750</td><td>831</td><td>296</td></tr>
                      <tr><td>14" (350) x 10" (250)</td><td>334</td><td>252</td><td>889</td><td>889</td><td>892</td><td>603</td><td>527</td><td>38.1</td><td>20</td><td>412.8</td><td>69.8</td><td>6.4</td><td>329</td><td>425</td><td>1000</td><td>750</td><td>599</td><td>295</td></tr>
                      <tr><td>14" (350)</td><td>334</td><td>334</td><td>889</td><td>889</td><td>892</td><td>603</td><td>527</td><td>38.1</td><td>20</td><td>412.8</td><td>69.8</td><td>6.4</td><td>399</td><td>516</td><td>1000</td><td>750</td><td>914</td><td>439</td></tr>
                      <tr><td>16" (400) x 14" (350)</td><td>385</td><td>334</td><td>991</td><td>991</td><td>994</td><td>686</td><td>603.2</td><td>41.1</td><td>20</td><td>469.9</td><td>76.2</td><td>6.4</td><td>399</td><td>516</td><td>1000</td><td>750</td><td>731</td><td>439</td></tr>
                      <tr><td>16" (400) x 12" (300)</td><td>385</td><td>303</td><td>991</td><td>991</td><td>994</td><td>686</td><td>603.2</td><td>41.1</td><td>20</td><td>469.9</td><td>76.2</td><td>6.4</td><td>381</td><td>478</td><td>1000</td><td>750</td><td>649</td><td>296</td></tr>
                      <tr><td>16" (400)</td><td>385</td><td>385</td><td>991</td><td>991</td><td>994</td><td>686</td><td>603.2</td><td>41.1</td><td>20</td><td>469.9</td><td>76.2</td><td>6.4</td><td>460</td><td>578</td><td>1000</td><td>1006</td><td>1387</td><td>439</td></tr>
                      <tr><td>18" (450) x 16" (400)</td><td>436</td><td>385</td><td>1092</td><td>1092</td><td>1095</td><td>743</td><td>654</td><td>44.5</td><td>20</td><td>533.4</td><td>82.6</td><td>6.4</td><td>460</td><td>578</td><td>1000</td><td>1006</td><td>1415</td><td>439</td></tr>
                      <tr><td>18" (450) x 14" (350)</td><td>436</td><td>334</td><td>1092</td><td>1092</td><td>1095</td><td>743</td><td>654</td><td>44.5</td><td>20</td><td>533.4</td><td>82.6</td><td>6.4</td><td>399</td><td>516</td><td>1000</td><td>750</td><td>1167</td><td>439</td></tr>
                      <tr><td>18" (450)</td><td>436</td><td>436</td><td>1092</td><td>1092</td><td>1095</td><td>743</td><td>654</td><td>44.5</td><td>20</td><td>533.4</td><td>82.6</td><td>6.4</td><td>518</td><td>677</td><td>1000</td><td>1006</td><td>1846</td><td>439</td></tr>
                      <tr><td>20" (500) x 18" (450)</td><td>487</td><td>436</td><td>1194</td><td>1194</td><td>1200</td><td>813</td><td>723.9</td><td>44.5</td><td>24</td><td>584.2</td><td>88.9</td><td>6.4</td><td>518</td><td>677</td><td>1000</td><td>1006</td><td>2131</td><td>439</td></tr>
                      <tr><td>20" (500) x 16" (400)</td><td>487</td><td>385</td><td>1194</td><td>1194</td><td>1200</td><td>813</td><td>723.9</td><td>44.5</td><td>24</td><td>584.2</td><td>88.9</td><td>6.4</td><td>460</td><td>578</td><td>1000</td><td>1006</td><td>1510</td><td>439</td></tr>
                      <tr><td>20" (500)</td><td>487</td><td>487</td><td>1194</td><td>1194</td><td>1200</td><td>813</td><td>723.9</td><td>44.5</td><td>24</td><td>584.2</td><td>88.9</td><td>6.4</td><td>622</td><td>699</td><td>1000</td><td>1006</td><td>2796</td><td>439</td></tr>
                      <tr><td>24" (600) x 20" (500)</td><td>589</td><td>487</td><td>1397</td><td>1397</td><td>1407</td><td>940</td><td>838.2</td><td>50.8</td><td>24</td><td>692.2</td><td>102</td><td>6.4</td><td>622</td><td>699</td><td>1000</td><td>1006</td><td>2941</td><td>439</td></tr>
                      <tr><td>24" (600)</td><td>589</td><td>589</td><td>1397</td><td>1397</td><td>1407</td><td>940</td><td>838.2</td><td>50.8</td><td>24</td><td>692.2</td><td>102</td><td>6.4</td><td>622</td><td>828</td><td>1000</td><td>1006</td><td>2501</td><td>439</td></tr>
                      <tr><td>24" (600) x 18" (450)</td><td>589</td><td>436</td><td>1397</td><td>1397</td><td>1407</td><td>940</td><td>838.2</td><td>50.8</td><td>24</td><td>692.2</td><td>102</td><td>6.4</td><td>518</td><td>677</td><td>1000</td><td>1006</td><td>3406</td><td>439</td></tr>
                      <tr><td>24" (600) x 16" (400)</td><td>589</td><td>385</td><td>1397</td><td>1397</td><td>1407</td><td>940</td><td>838.2</td><td>50.8</td><td>24</td><td>692.2</td><td>102</td><td>6.4</td><td>460</td><td>578</td><td>1000</td><td>1006</td><td>3428</td><td>439</td></tr>
                      <tr><td>24" (600)</td><td>589</td><td>589</td><td>1397</td><td>1397</td><td>1407</td><td>940</td><td>838.2</td><td>50.8</td><td>24</td><td>692.2</td><td>102</td><td>6.4</td><td>622</td><td>828</td><td>1000</td><td>1006</td><td>3980</td><td>439</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 900 Class Table */}
              <div className="tmbv-dimensional-table-wrapper">
                <h3 className="tmbv-dimensional-title">TRUNNION MOUNTED BALL VALVE DIMENSIONAL DETAILS FOR 900 CLASS</h3>
                <div className="tmbv-table-scroll-container">
                  <table className="tmbv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th>ØB1</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>H1</th>
                        <th>O</th>
                        <th>G</th>
                        <th>ØC</th>
                        <th colSpan="2">Weight (Kg)</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>Bare Stem</th>
                        <th>Gear</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050) x 1 1/2" (040)</td><td>49</td><td>38</td><td>368</td><td>368</td><td>371</td><td>216</td><td>165.1</td><td>25.4</td><td>8</td><td>91.9</td><td>38.1</td><td>6.4</td><td>110</td><td>143</td><td>635</td><td>-</td><td>40</td><td>-</td></tr>
                      <tr><td>2" (050)</td><td>49</td><td>49</td><td>368</td><td>368</td><td>371</td><td>216</td><td>165.1</td><td>25.4</td><td>8</td><td>91.9</td><td>38.1</td><td>6.4</td><td>110</td><td>143</td><td>635</td><td>-</td><td>40</td><td>-</td></tr>
                      <tr><td>3" (080) x 2" (050)</td><td>74</td><td>49</td><td>381</td><td>381</td><td>384</td><td>241</td><td>190.5</td><td>25.4</td><td>8</td><td>127</td><td>38.1</td><td>6.4</td><td>110</td><td>143</td><td>635</td><td>-</td><td>58</td><td>-</td></tr>
                      <tr><td>3" (080)</td><td>74</td><td>74</td><td>381</td><td>381</td><td>384</td><td>241</td><td>190.5</td><td>25.4</td><td>8</td><td>127</td><td>38.1</td><td>6.4</td><td>199</td><td>259</td><td>-</td><td>350</td><td>110</td><td>23</td></tr>
                      <tr><td>4" (100) x 3" (080)</td><td>99</td><td>74</td><td>457</td><td>457</td><td>460</td><td>292</td><td>235</td><td>31.8</td><td>8</td><td>157.2</td><td>44.5</td><td>6.4</td><td>199</td><td>259</td><td>-</td><td>350</td><td>110</td><td>23</td></tr>
                      <tr><td>4" (100)</td><td>99</td><td>99</td><td>457</td><td>457</td><td>460</td><td>292</td><td>235</td><td>31.8</td><td>8</td><td>157.2</td><td>44.5</td><td>6.4</td><td>214</td><td>275</td><td>-</td><td>500</td><td>147</td><td>23</td></tr>
                      <tr><td>6" (150) x 4" (100)</td><td>147</td><td>99</td><td>610</td><td>610</td><td>613</td><td>381</td><td>317.5</td><td>31.8</td><td>12</td><td>215.9</td><td>55.6</td><td>6.4</td><td>214</td><td>275</td><td>-</td><td>500</td><td>242</td><td>23</td></tr>
                      <tr><td>6" (150)</td><td>147</td><td>147</td><td>610</td><td>610</td><td>613</td><td>381</td><td>317.5</td><td>31.8</td><td>12</td><td>215.9</td><td>55.6</td><td>6.4</td><td>241</td><td>312</td><td>-</td><td>600</td><td>360</td><td>43.9</td></tr>
                      <tr><td>8" (200) x 6" (150)</td><td>191</td><td>147</td><td>737</td><td>737</td><td>740</td><td>470</td><td>393.7</td><td>38.1</td><td>12</td><td>269.7</td><td>63.5</td><td>6.4</td><td>241</td><td>312</td><td>-</td><td>600</td><td>599</td><td>43.9</td></tr>
                      <tr><td>8" (200)</td><td>191</td><td>191</td><td>737</td><td>737</td><td>740</td><td>470</td><td>393.7</td><td>38.1</td><td>12</td><td>269.7</td><td>63.5</td><td>6.4</td><td>269</td><td>340</td><td>-</td><td>750</td><td>641</td><td>90</td></tr>
                      <tr><td>10" (250) x 8" (200)</td><td>238</td><td>191</td><td>838</td><td>838</td><td>841</td><td>546</td><td>469.9</td><td>38.1</td><td>16</td><td>323.9</td><td>69.8</td><td>6.4</td><td>269</td><td>340</td><td>-</td><td>750</td><td>893</td><td>90</td></tr>
                      <tr><td>10" (250)</td><td>238</td><td>238</td><td>838</td><td>838</td><td>841</td><td>546</td><td>469.9</td><td>38.1</td><td>16</td><td>323.9</td><td>69.8</td><td>6.4</td><td>329</td><td>425</td><td>-</td><td>750</td><td>1103</td><td>114</td></tr>
                      <tr><td>12" (300) x 10" (250)</td><td>283</td><td>238</td><td>965</td><td>965</td><td>968</td><td>610</td><td>533.4</td><td>38.1</td><td>20</td><td>381</td><td>79.2</td><td>6.4</td><td>329</td><td>425</td><td>-</td><td>750</td><td>1365</td><td>114</td></tr>
                      <tr><td>12" (300)</td><td>283</td><td>283</td><td>965</td><td>965</td><td>968</td><td>610</td><td>533.4</td><td>38.1</td><td>20</td><td>381</td><td>79.2</td><td>6.4</td><td>381</td><td>478</td><td>-</td><td>750</td><td>1628</td><td>114</td></tr>
                      <tr><td>14" (350) x 12" (300)</td><td>315</td><td>287</td><td>1029</td><td>1029</td><td>1038</td><td>641</td><td>558.8</td><td>41.5</td><td>20</td><td>412.8</td><td>85.9</td><td>6.4</td><td>381</td><td>478</td><td>-</td><td>750</td><td>1367</td><td>137</td></tr>
                      <tr><td>14" (350) x 10" (250)</td><td>315</td><td>238</td><td>1029</td><td>1029</td><td>1038</td><td>641</td><td>558.8</td><td>41.5</td><td>20</td><td>412.8</td><td>85.9</td><td>6.4</td><td>329</td><td>425</td><td>-</td><td>750</td><td>1103</td><td>114</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 1500 Class Table */}
              <div className="tmbv-dimensional-table-wrapper">
                <h3 className="tmbv-dimensional-title">TRUNNION MOUNTED BALL VALVE DIMENSIONAL DETAILS FOR 1500 CLASS</h3>
                <div className="tmbv-table-scroll-container">
                  <table className="tmbv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th>ØB1</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>H1</th>
                        <th>O</th>
                        <th>G</th>
                        <th>ØC</th>
                        <th colSpan="2">Weight (Kg)</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>Bare Stem</th>
                        <th>Gear</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050) x 1 1/2" (040)</td><td>49</td><td>38</td><td>368</td><td>368</td><td>371</td><td>216</td><td>165.1</td><td>25.4</td><td>8</td><td>91.9</td><td>38.1</td><td>6.4</td><td>110</td><td>143</td><td>635</td><td>-</td><td>50</td><td>-</td></tr>
                      <tr><td>2" (050)</td><td>49</td><td>49</td><td>368</td><td>368</td><td>371</td><td>216</td><td>165.1</td><td>25.4</td><td>8</td><td>91.9</td><td>38.1</td><td>6.4</td><td>110</td><td>143</td><td>635</td><td>-</td><td>50</td><td>-</td></tr>
                      <tr><td>3" (080) x 2" (050)</td><td>70</td><td>49</td><td>470</td><td>470</td><td>473</td><td>267</td><td>203.2</td><td>31.8</td><td>8</td><td>127</td><td>47.8</td><td>6.4</td><td>110</td><td>143</td><td>635</td><td>-</td><td>105</td><td>-</td></tr>
                      <tr><td>3" (080)</td><td>70</td><td>70</td><td>470</td><td>470</td><td>473</td><td>267</td><td>203.2</td><td>31.8</td><td>8</td><td>127</td><td>47.8</td><td>6.4</td><td>199</td><td>259</td><td>-</td><td>350</td><td>110</td><td>23</td></tr>
                      <tr><td>4" (100) x 3" (080)</td><td>92</td><td>70</td><td>546</td><td>546</td><td>549</td><td>311</td><td>241.3</td><td>35.1</td><td>8</td><td>157.2</td><td>53.8</td><td>6.4</td><td>199</td><td>259</td><td>-</td><td>350</td><td>147</td><td>23</td></tr>
                      <tr><td>4" (100)</td><td>92</td><td>92</td><td>546</td><td>546</td><td>549</td><td>311</td><td>241.3</td><td>35.1</td><td>8</td><td>157.2</td><td>53.8</td><td>6.4</td><td>214</td><td>275</td><td>-</td><td>500</td><td>147</td><td>30</td></tr>
                      <tr><td>6" (150) x 4" (100)</td><td>137</td><td>92</td><td>705</td><td>705</td><td>711</td><td>394</td><td>317.5</td><td>38.1</td><td>12</td><td>215.9</td><td>82.6</td><td>6.4</td><td>214</td><td>275</td><td>-</td><td>500</td><td>360</td><td>30</td></tr>
                      <tr><td>6" (150)</td><td>137</td><td>137</td><td>705</td><td>705</td><td>711</td><td>394</td><td>317.5</td><td>38.1</td><td>12</td><td>215.9</td><td>82.6</td><td>6.4</td><td>241</td><td>312</td><td>-</td><td>600</td><td>360</td><td>90</td></tr>
                      <tr><td>8" (200) x 6" (150)</td><td>178</td><td>137</td><td>832</td><td>832</td><td>842</td><td>483</td><td>393.7</td><td>41.1</td><td>12</td><td>269.7</td><td>91.9</td><td>6.4</td><td>241</td><td>312</td><td>-</td><td>600</td><td>641</td><td>90</td></tr>
                      <tr><td>8" (200)</td><td>178</td><td>178</td><td>832</td><td>832</td><td>842</td><td>483</td><td>393.7</td><td>41.1</td><td>12</td><td>269.7</td><td>91.9</td><td>6.4</td><td>269</td><td>340</td><td>-</td><td>750</td><td>641</td><td>114</td></tr>
                      <tr><td>10" (250) x 8" (200)</td><td>223</td><td>178</td><td>991</td><td>991</td><td>1001</td><td>584</td><td>482.6</td><td>50.8</td><td>12</td><td>323.9</td><td>108</td><td>6.4</td><td>269</td><td>340</td><td>-</td><td>750</td><td>1103</td><td>114</td></tr>
                      <tr><td>10" (250)</td><td>223</td><td>223</td><td>991</td><td>991</td><td>1001</td><td>584</td><td>482.6</td><td>50.8</td><td>12</td><td>323.9</td><td>108</td><td>6.4</td><td>329</td><td>425</td><td>-</td><td>750</td><td>1103</td><td>137</td></tr>
                      <tr><td>12" (300) x 10" (250)</td><td>264</td><td>223</td><td>1130</td><td>1130</td><td>1146</td><td>673</td><td>571.5</td><td>53.9</td><td>16</td><td>381</td><td>124</td><td>6.4</td><td>329</td><td>425</td><td>-</td><td>750</td><td>1628</td><td>137</td></tr>
                      <tr><td>12" (300)</td><td>264</td><td>264</td><td>1130</td><td>1130</td><td>1146</td><td>673</td><td>571.5</td><td>53.9</td><td>16</td><td>381</td><td>124</td><td>6.4</td><td>381</td><td>478</td><td>-</td><td>750</td><td>1628</td><td>220</td></tr>
                      <tr><td>14" (350) x 12" (300)</td><td>315</td><td>287</td><td>1257</td><td>1257</td><td>1276</td><td>749</td><td>635</td><td>60.3</td><td>16</td><td>412.8</td><td>133.4</td><td>6.4</td><td>381</td><td>478</td><td>-</td><td>600</td><td>2927</td><td>220</td></tr>
                      <tr><td>14" (350) x 10" (250)</td><td>315</td><td>223</td><td>1257</td><td>1257</td><td>1276</td><td>749</td><td>635</td><td>60.3</td><td>16</td><td>412.8</td><td>133.4</td><td>6.4</td><td>329</td><td>425</td><td>-</td><td>600</td><td>1103</td><td>137</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 2500 Class Table */}
              <div className="tmbv-dimensional-table-wrapper">
                <h3 className="tmbv-dimensional-title">TRUNNION MOUNTED BALL VALVE DIMENSIONAL DETAILS FOR 2500 CLASS</h3>
                <div className="tmbv-table-scroll-container">
                  <table className="tmbv-dimensional-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>ØB</th>
                        <th>ØB1</th>
                        <th colSpan="3">L</th>
                        <th>ØD</th>
                        <th>ØPCD</th>
                        <th>ØH</th>
                        <th>N</th>
                        <th>ØE</th>
                        <th>T</th>
                        <th>RF</th>
                        <th>H1</th>
                        <th>O</th>
                        <th>G</th>
                        <th>ØC</th>
                        <th colSpan="2">Weight (Kg)</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>RF</th>
                        <th>BWE</th>
                        <th>RTJ</th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th>Bare Stem</th>
                        <th>Gear</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>2" (050) x 1 1/2" (040)</td><td>42</td><td>38</td><td>451</td><td>451</td><td>454</td><td>235</td><td>171.4</td><td>28.6</td><td>8</td><td>91.9</td><td>50.8</td><td>6.4</td><td>145</td><td>200</td><td>900</td><td>-</td><td>72</td><td>-</td></tr>
                      <tr><td>2" (050)</td><td>42</td><td>42</td><td>451</td><td>451</td><td>454</td><td>235</td><td>171.4</td><td>28.6</td><td>8</td><td>91.9</td><td>50.8</td><td>6.4</td><td>145</td><td>200</td><td>900</td><td>-</td><td>72</td><td>-</td></tr>
                      <tr><td>3" (080) x 2" (050)</td><td>58</td><td>42</td><td>578</td><td>578</td><td>584</td><td>305</td><td>228.6</td><td>35.1</td><td>8</td><td>127</td><td>66.5</td><td>6.4</td><td>145</td><td>200</td><td>900</td><td>-</td><td>163</td><td>-</td></tr>
                      <tr><td>3" (080)</td><td>58</td><td>58</td><td>578</td><td>578</td><td>584</td><td>305</td><td>228.6</td><td>35.1</td><td>8</td><td>127</td><td>66.5</td><td>6.4</td><td>233</td><td>287</td><td>-</td><td>300</td><td>163</td><td>30</td></tr>
                      <tr><td>4" (100) x 3" (080)</td><td>73</td><td>58</td><td>673</td><td>673</td><td>683</td><td>356</td><td>273</td><td>41.3</td><td>8</td><td>157.2</td><td>76.2</td><td>6.4</td><td>233</td><td>287</td><td>-</td><td>300</td><td>299</td><td>30</td></tr>
                      <tr><td>4" (100)</td><td>87</td><td>87</td><td>673</td><td>673</td><td>683</td><td>356</td><td>273</td><td>41.3</td><td>8</td><td>157.2</td><td>76.2</td><td>6.4</td><td>233</td><td>287</td><td>-</td><td>500</td><td>443</td><td>30</td></tr>
                      <tr><td>6" (150) x 4" (100)</td><td>111</td><td>87</td><td>914</td><td>914</td><td>927</td><td>483</td><td>368.3</td><td>53.9</td><td>8</td><td>215.9</td><td>108.0</td><td>6.4</td><td>233</td><td>287</td><td>-</td><td>500</td><td>588</td><td>30</td></tr>
                      <tr><td>6" (150)</td><td>111</td><td>111</td><td>914</td><td>914</td><td>927</td><td>483</td><td>368.3</td><td>53.9</td><td>8</td><td>215.9</td><td>108.0</td><td>6.4</td><td>305</td><td>381</td><td>-</td><td>500</td><td>588</td><td>90</td></tr>
                      <tr><td>8" (200) x 6" (150)</td><td>147</td><td>111</td><td>1022</td><td>1022</td><td>1038</td><td>552</td><td>438.2</td><td>53.9</td><td>12</td><td>269.7</td><td>127.0</td><td>6.4</td><td>305</td><td>381</td><td>-</td><td>500</td><td>1071</td><td>90</td></tr>
                      <tr><td>8" (200)</td><td>147</td><td>147</td><td>1022</td><td>1022</td><td>1038</td><td>552</td><td>438.2</td><td>53.9</td><td>12</td><td>269.7</td><td>127.0</td><td>6.4</td><td>381</td><td>478</td><td>-</td><td>700</td><td>1071</td><td>220</td></tr>
                      <tr><td>10" (250) x 8" (200)</td><td>185</td><td>147</td><td>1270</td><td>1270</td><td>1292</td><td>673</td><td>539.8</td><td>66.7</td><td>12</td><td>323.9</td><td>165.1</td><td>6.4</td><td>381</td><td>478</td><td>-</td><td>700</td><td>1676</td><td>220</td></tr>
                      <tr><td>10" (250)</td><td>185</td><td>185</td><td>1270</td><td>1270</td><td>1292</td><td>673</td><td>539.8</td><td>66.7</td><td>12</td><td>323.9</td><td>165.1</td><td>6.4</td><td>460</td><td>578</td><td>-</td><td>700</td><td>1676</td><td>220</td></tr>
                      <tr><td>12" (300) x 10" (250)</td><td>219</td><td>185</td><td>1422</td><td>1422</td><td>1444</td><td>762</td><td>619.3</td><td>73</td><td>12</td><td>381</td><td>184.2</td><td>6.4</td><td>460</td><td>578</td><td>-</td><td>700</td><td>2772</td><td>220</td></tr>
                      <tr><td>12" (300)</td><td>219</td><td>219</td><td>1422</td><td>1422</td><td>1444</td><td>762</td><td>619.3</td><td>73</td><td>12</td><td>381</td><td>184.2</td><td>6.4</td><td>559</td><td>514</td><td>-</td><td>700</td><td>3618</td><td>220</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      

      


     

     

      {/* Torque Values Section */}
      <div 
        ref={(el) => (sectionsRef.current[2] = el)}
        className="scroll-section"
      >
        <section className="tmbv-torque-section">
          <div className="tmbv-container">
            <h2 className="tmbv-table-title">Torque Values in Nm</h2>
            <div className="tmbv-table-scroll-container">
              <table className="tmbv-torque-table">
                <thead>
                  <tr>
                    <th rowSpan="2">Size in NPS (DN)</th>
                    <th rowSpan="2">Torque Type</th>
                    <th colSpan="6">Torque Values in Nm</th>
                  </tr>
                  <tr>
                    <th>150</th>
                    <th>300</th>
                    <th>600</th>
                    <th>900</th>
                    <th>1500</th>
                    <th>2500</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td rowSpan="3">1 1/2" (040)</td><td>BTO</td><td>48</td><td>71</td><td>87</td><td>99</td><td>146</td><td>280</td></tr>
                  <tr><td>ETC</td><td>28</td><td>57</td><td>70</td><td>79</td><td>117</td><td>224</td></tr>
                  <tr><td>RUN</td><td>21</td><td>32</td><td>39</td><td>44</td><td>65</td><td>125</td></tr>
                  <tr><td rowSpan="3">2" (050)</td><td>BTO</td><td>69</td><td>85</td><td>110</td><td>135</td><td>257</td><td>365</td></tr>
                  <tr><td>ETC</td><td>55</td><td>68</td><td>88</td><td>108</td><td>206</td><td>292</td></tr>
                  <tr><td>RUN</td><td>31</td><td>38</td><td>49</td><td>60</td><td>115</td><td>163</td></tr>
                  <tr><td rowSpan="3">3" (080)</td><td>BTO</td><td>160</td><td>220</td><td>323</td><td>436</td><td>605</td><td>1153</td></tr>
                  <tr><td>ETC</td><td>128</td><td>176</td><td>258</td><td>349</td><td>464</td><td>922</td></tr>
                  <tr><td>RUN</td><td>71</td><td>95</td><td>144</td><td>195</td><td>270</td><td>515</td></tr>
                  <tr><td rowSpan="3">4" (100)</td><td>BTO</td><td>242</td><td>340</td><td>465</td><td>605</td><td>931</td><td>1598</td></tr>
                  <tr><td>ETC</td><td>194</td><td>272</td><td>272</td><td>484</td><td>745</td><td>1278</td></tr>
                  <tr><td>RUN</td><td>108</td><td>162</td><td>208</td><td>270</td><td>416</td><td>714</td></tr>
                  <tr><td rowSpan="3">6" (150)</td><td>BTO</td><td>418</td><td>665</td><td>1078</td><td>1498</td><td>2580</td><td>3086</td></tr>
                  <tr><td>ETC</td><td>337</td><td>535</td><td>862</td><td>1198</td><td>2064</td><td>2469</td></tr>
                  <tr><td>RUN</td><td>187</td><td>297</td><td>462</td><td>669</td><td>1152</td><td>1378</td></tr>
                  <tr><td rowSpan="3">8" (200)</td><td>BTO</td><td>818</td><td>1328</td><td>2126</td><td>3188</td><td>4941</td><td>7775</td></tr>
                  <tr><td>ETC</td><td>654</td><td>1062</td><td>1701</td><td>2550</td><td>3953</td><td>6220</td></tr>
                  <tr><td>RUN</td><td>365</td><td>593</td><td>950</td><td>1424</td><td>2207</td><td>3473</td></tr>
                  <tr><td rowSpan="3">10" (250)</td><td>BTO</td><td>1094</td><td>1713</td><td>2825</td><td>3955</td><td>6130</td><td>-</td></tr>
                  <tr><td>ETC</td><td>875</td><td>1370</td><td>2260</td><td>3164</td><td>4904</td><td>-</td></tr>
                  <tr><td>RUN</td><td>489</td><td>765</td><td>1262</td><td>1767</td><td>2738</td><td>-</td></tr>
                  <tr><td rowSpan="3">12" (300)</td><td>BTO</td><td>1412</td><td>2047</td><td>3275</td><td>4913</td><td>8843</td><td>-</td></tr>
                  <tr><td>ETC</td><td>1130</td><td>1638</td><td>2620</td><td>3930</td><td>7074</td><td>-</td></tr>
                  <tr><td>RUN</td><td>631</td><td>914</td><td>1463</td><td>2194</td><td>3950</td><td>-</td></tr>
                  <tr><td rowSpan="3">14" (350)</td><td>BTO</td><td>2484</td><td>3479</td><td>5565</td><td>8348</td><td>16579</td><td>-</td></tr>
                  <tr><td>ETC</td><td>1987</td><td>2783</td><td>4452</td><td>6678</td><td>13283</td><td>-</td></tr>
                  <tr><td>RUN</td><td>1110</td><td>1554</td><td>2456</td><td>3729</td><td>7405</td><td>-</td></tr>
                  <tr><td rowSpan="3">16" (400)</td><td>BTO</td><td>3380</td><td>4780</td><td>7063</td><td>10373</td><td>22231</td><td>-</td></tr>
                  <tr><td>ETC</td><td>2704</td><td>3824</td><td>5850</td><td>8298</td><td>17785</td><td>-</td></tr>
                  <tr><td>RUN</td><td>1510</td><td>2135</td><td>3155</td><td>4633</td><td>9930</td><td>-</td></tr>
                  <tr><td rowSpan="3">18" (450)</td><td>BTO</td><td>4522</td><td>7118</td><td>11387</td><td>17081</td><td>-</td><td>-</td></tr>
                  <tr><td>ETC</td><td>3618</td><td>5894</td><td>9110</td><td>13665</td><td>-</td><td>-</td></tr>
                  <tr><td>RUN</td><td>2020</td><td>3179</td><td>5086</td><td>7630</td><td>-</td><td>-</td></tr>
                  <tr><td rowSpan="3">20" (500)</td><td>BTO</td><td>5731</td><td>9863</td><td>15780</td><td>23670</td><td>-</td><td>-</td></tr>
                  <tr><td>ETC</td><td>4585</td><td>7890</td><td>12624</td><td>18936</td><td>-</td><td>-</td></tr>
                  <tr><td>RUN</td><td>2560</td><td>4405</td><td>7048</td><td>10573</td><td>-</td><td>-</td></tr>
                  <tr><td rowSpan="3">24" (600)</td><td>BTO</td><td>10449</td><td>16468</td><td>25233</td><td>39525</td><td>-</td><td>-</td></tr>
                  <tr><td>ETC</td><td>8359</td><td>13174</td><td>20186</td><td>31620</td><td>-</td><td>-</td></tr>
                  <tr><td>RUN</td><td>4667</td><td>7356</td><td>11271</td><td>17655</td><td>-</td><td>-</td></tr>
                </tbody>
              </table>
            </div>
            
            {/* Torque Values Notes */}
            <div className="tmbv-torque-notes">
              <h3 className="tmbv-torque-notes-title">Notes -</h3>
              <ol className="tmbv-torque-notes-list">
                <li>Torques Values are in Nm and are for Primary Soft Seated TMBVs with Seat Insert Material as Nylon PA - 12/ Devlon V (API).</li>
                <li>Torque Values with PEEK seat insert are 100% higher than the corresponding above mentioned values.</li>
                <li>Torque Values are at Ambient Temperature, media being clear water. (Without any safety factor)</li>
                <li>For Reduced Bore Valves, take torque values corresponding to the lower size (Ball Bore size) e.g. : for 6" x 4" Reduced Bore Valve, take torque values corresponding to 4".</li>
                <li>Torque details for balance sizes can be provided on request.</li>
                <li>Above Torque Values are indicative and for reference only. Exact Torque Values for Actuator sizing will depend on service conditions</li>
              </ol>
              <div className="tmbv-torque-legend">
                <p><strong>BTO - BREAK TO OPEN</strong></p>
                <p><strong>ETC - END TO CLOSE</strong></p>
                <p><strong>RUN - RUNNING</strong></p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <InquiryBanner />
      <Footer />
      {scrollY > 300 && (
        <button 
          className="scroll-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            cursor: 'pointer',
            fontSize: '1.2rem',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
            transition: 'all 0.3s ease'
          }}
        >
          ↑
        </button>
      )}
    </>
  );
};

export default TrunnionBallValves;

